import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";

const FIRST_LOGIN_KEY = "ease_first_login";
const DISMISSED_KEY = "ease_dismissed_new_badges";
const REVEALED_PATHS_KEY = "ease_revealed_paths";
const FEATURE_REVEAL_BYPASS_ROLES = new Set(["super_admin", "admin", "customer_success", "staff"]);

/** Modules available at each lifecycle stage */
const REVEAL_STAGES: Array<{ afterDays: number; paths: string[] }> = [
  { afterDays: 0, paths: ["/", "/dashboard", "/inbox", "/crm", "/email", "/workflows", "/settings"] },
  { afterDays: 7, paths: ["/services", "/pages", "/analytics"] },
  { afterDays: 14, paths: ["/webinars", "/communications", "/reputation"] },
  { afterDays: 30, paths: ["/ai", "/briefing", "/courses", "/marketplace"] },
];

const MODULE_PATHS: Record<string, string[]> = {
  crm: ["/crm"],
  email: ["/email"],
  bookings: ["/services"],
  services: ["/services"],
  pages: ["/pages"],
  workflows: ["/workflows"],
  analytics: ["/analytics"],
  webinars: ["/webinars"],
  communications: ["/communications"],
  reputation: ["/reputation"],
  ai: ["/ai"],
  briefing: ["/briefing"],
  community: ["/community"],
  communities: ["/community"],
  learning: ["/courses"],
  courses: ["/courses"],
  marketplace: ["/marketplace"],
  affiliate: ["/affiliate"],
  affiliates: ["/affiliate"],
};

const ALWAYS_REVEALED = ["/", "/dashboard", "/inbox", "/settings"];
const LEVEL_TWO_PATHS = ["/services", "/pages"];
const LEVEL_THREE_PATHS = ["/workflows", "/analytics"];
const LEVEL_FOUR_PATHS = ["/webinars", "/communications", "/reputation", "/ai", "/briefing", "/community", "/courses", "/marketplace"];

interface SidebarTemplateConfig {
  sidebarModules?: string[];
}

interface OnboardingSidebarState {
  featureRevealLevel?: number;
}

interface WorkspaceContextResponse {
  activeTenant: {
    enabledModules?: string[];
  } | null;
}

function getFirstLoginDate(): Date {
  const stored = localStorage.getItem(FIRST_LOGIN_KEY);
  if (stored) return new Date(stored);
  const now = new Date();
  localStorage.setItem(FIRST_LOGIN_KEY, now.toISOString());
  return now;
}

function getDaysSinceFirstLogin(): number {
  const first = getFirstLoginDate();
  const now = new Date();
  return Math.floor((now.getTime() - first.getTime()) / (1000 * 60 * 60 * 24));
}

function getDismissedBadges(): Set<string> {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissedBadges(dismissed: Set<string>) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed]));
}

function getStoredRevealedPaths(): Set<string> {
  try {
    const stored = localStorage.getItem(REVEALED_PATHS_KEY);
    return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveRevealedPaths(paths: Set<string>) {
  localStorage.setItem(REVEALED_PATHS_KEY, JSON.stringify([...paths]));
}

function buildTenantRevealPaths(sidebarModules: string[], featureRevealLevel: number): Set<string> {
  const revealed = new Set<string>(ALWAYS_REVEALED);

  for (const module of sidebarModules) {
    for (const path of MODULE_PATHS[module] ?? []) {
      revealed.add(path);
    }
  }

  if (featureRevealLevel >= 2) {
    for (const path of LEVEL_TWO_PATHS) revealed.add(path);
  }
  if (featureRevealLevel >= 3) {
    for (const path of LEVEL_THREE_PATHS) revealed.add(path);
  }
  if (featureRevealLevel >= 4) {
    for (const path of LEVEL_FOUR_PATHS) revealed.add(path);
  }

  return revealed;
}

function sidebarModulesFromEnabledModules(enabledModules: readonly string[]): string[] {
  const modules = new Set<string>();

  for (const module of enabledModules) {
    if (module === "learning") {
      modules.add("courses");
      continue;
    }
    modules.add(module);
  }

  return [...modules];
}

function buildFallbackRevealState() {
  const daysSinceFirstLogin = getDaysSinceFirstLogin();
  const dismissed = getDismissedBadges();
  const revealedPaths = new Set<string>();
  const newPaths = new Set<string>();

  for (const stage of REVEAL_STAGES) {
    if (daysSinceFirstLogin >= stage.afterDays) {
      for (const path of stage.paths) {
        revealedPaths.add(path);
        if (
          daysSinceFirstLogin >= stage.afterDays &&
          daysSinceFirstLogin < stage.afterDays + 3 &&
          stage.afterDays > 0 &&
          !dismissed.has(path)
        ) {
          newPaths.add(path);
        }
      }
    }
  }

  return { daysSinceFirstLogin, revealedPaths, newPaths };
}

export function canBypassFeatureReveal(role: string | null | undefined): boolean {
  return FEATURE_REVEAL_BYPASS_ROLES.has(role?.trim().toLowerCase() ?? "");
}

export interface FeatureRevealState {
  /** Paths currently revealed to the user */
  revealedPaths: Set<string>;
  /** Paths that are newly revealed (show [NEW] badge) */
  newPaths: Set<string>;
  /** Check if a path is revealed */
  isRevealed: (path: string) => boolean;
  /** Check if a path should show [NEW] badge */
  isNew: (path: string) => boolean;
  /** Dismiss the [NEW] badge for a path */
  dismissBadge: (path: string) => void;
  /** Days since first login */
  daysSinceFirstLogin: number;
}

export function useFeatureReveal(): FeatureRevealState {
  const { isAuthenticated, user } = useAuth();
  const shouldBypassFeatureReveal = canBypassFeatureReveal(user?.role);
  const dismissed = useMemo(() => getDismissedBadges(), []);
  const fallback = useMemo(() => buildFallbackRevealState(), []);
  const [revealedPaths, setRevealedPaths] = useState<Set<string>>(fallback.revealedPaths);
  const [newPaths, setNewPaths] = useState<Set<string>>(fallback.newPaths);

  useEffect(() => {
    if (!isAuthenticated || shouldBypassFeatureReveal) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const workspaceResponse = await apiFetch<{ data: WorkspaceContextResponse }>("/v1/platform/workspace-context");
        const enabledModules = workspaceResponse.data.activeTenant?.enabledModules ?? [];
        if (!enabledModules.includes("crm")) {
          const sidebarModules = sidebarModulesFromEnabledModules(enabledModules);
          const nextRevealed = buildTenantRevealPaths(sidebarModules, 1);
          const previousRevealed = getStoredRevealedPaths();
          const nextNew = new Set<string>();

          for (const path of nextRevealed) {
            if (!previousRevealed.has(path) && !dismissed.has(path) && !ALWAYS_REVEALED.includes(path)) {
              nextNew.add(path);
            }
          }

          if (cancelled) {
            return;
          }

          saveRevealedPaths(nextRevealed);
          setRevealedPaths(nextRevealed);
          setNewPaths(nextNew);
          return;
        }

        // Tenant context resolved server-side via tenantResolver middleware (session cookie).
        // No tenant ID in URL — per CLAUDE.md architecture.
        const [templateResponse, onboardingResponse] = await Promise.all([
          apiFetch<{ value: SidebarTemplateConfig }>("/platform/config/resolve/crm/vertical_template"),
          apiFetch<{ data: OnboardingSidebarState }>("/crm/onboarding/checklist/state"),
        ]);

        if (cancelled) {
          return;
        }

        // When no vertical template is configured (null/undefined), show ALL modules
        // so existing users and admins see the full platform, not a restricted onboarding view.
        const ALL_MODULES = ["crm", "email", "bookings", "pages", "workflows", "analytics", "webinars", "communities", "courses", "marketplace", "services", "settings"];
        const sidebarModules = Array.isArray(templateResponse.value?.sidebarModules)
          ? templateResponse.value.sidebarModules
          : ALL_MODULES;
        // Default to level 4 (all revealed) when not configured — prevents hiding modules for existing users
        const featureRevealLevel = Math.max(1, Math.min(4, onboardingResponse.data?.featureRevealLevel ?? 4));
        const nextRevealed = buildTenantRevealPaths(sidebarModules, featureRevealLevel);
        const previousRevealed = getStoredRevealedPaths();
        const nextNew = new Set<string>();

        for (const path of nextRevealed) {
          if (!previousRevealed.has(path) && !dismissed.has(path) && !ALWAYS_REVEALED.includes(path)) {
            nextNew.add(path);
          }
        }

        saveRevealedPaths(nextRevealed);
        setRevealedPaths(nextRevealed);
        setNewPaths(nextNew);
      } catch {
        // Keep the existing fallback reveal behavior when tenant-aware state is unavailable.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dismissed, isAuthenticated, shouldBypassFeatureReveal]);

  const isRevealed = (path: string) => shouldBypassFeatureReveal || revealedPaths.has(path);
  const isNew = (path: string) => !shouldBypassFeatureReveal && newPaths.has(path);

  const dismissBadge = (path: string) => {
    dismissed.add(path);
    saveDismissedBadges(dismissed);
    setNewPaths((current) => {
      const next = new Set(current);
      next.delete(path);
      return next;
    });
  };

  return { revealedPaths, newPaths, isRevealed, isNew, dismissBadge, daysSinceFirstLogin: fallback.daysSinceFirstLogin };
}
